import { Card } from "@/components/ui/card";
import { Sparkles, X, TrendingUp, Lightbulb, AlertCircle, CheckCircle2, Zap, ZapOff } from "lucide-react";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Badge } from "@/components/ui/badge";
import { useState, useEffect } from "react";
import DOMPurify from "dompurify";

interface AIInsightsProps {
  insights: string;
  onClose: () => void;
  onRegenerate?: () => void;
}

// Secure markdown parser with sanitization
const parseMarkdown = (text: string) => {
  const html = text
    .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
    .replace(/\*(.+?)\*/g, '<em>$1</em>');
  
  return DOMPurify.sanitize(html, { 
    ALLOWED_TAGS: ['strong', 'em', 'p', 'br'] 
  });
};

export const AIInsights = ({ insights, onClose, onRegenerate }: AIInsightsProps) => {
  // Gérer l'état des animations avec persistance
  const [animationsEnabled, setAnimationsEnabled] = useState(() => {
    const saved = localStorage.getItem('ai-insights-animations');
    return saved !== 'false'; // Par défaut activé
  });

  useEffect(() => {
    localStorage.setItem('ai-insights-animations', String(animationsEnabled));
  }, [animationsEnabled]);

  // Diviser les insights en points et catégoriser
  const insightPoints = (insights || '')
    .split('\n')
    .filter(line => line && line.trim().length > 0)
    .map(line => line.replace(/^[-•*]\s*/, '').trim());

  const categorizeInsight = (insight: string) => {
    const lower = insight.toLowerCase();
    if (lower.includes('tendance') || lower.includes('croissance') || lower.includes('augmentation')) {
      return { type: 'trend', icon: TrendingUp, color: 'text-emerald-500' };
    }
    if (lower.includes('recommandation') || lower.includes('conseil') || lower.includes('optimiser')) {
      return { type: 'suggestion', icon: Lightbulb, color: 'text-amber-500' };
    }
    if (lower.includes('attention') || lower.includes('baisse') || lower.includes('diminution')) {
      return { type: 'warning', icon: AlertCircle, color: 'text-orange-500' };
    }
    return { type: 'info', icon: CheckCircle2, color: 'text-blue-500' };
  };

  return (
    <Card className={`relative overflow-hidden p-4 h-full flex flex-col bg-gradient-to-br from-[hsl(var(--ai-primary)/0.05)] via-[hsl(var(--ai-secondary)/0.05)] to-[hsl(var(--ai-accent)/0.05)] border-2 border-[hsl(var(--ai-primary)/0.2)] ${animationsEnabled ? 'ai-glow' : ''}`}>
      {/* Animated background shimmer */}
      {animationsEnabled && (
        <div className="absolute inset-0 ai-shimmer opacity-50 pointer-events-none" />
      )}
      
      <div className="relative flex items-start justify-between gap-3 mb-4 flex-shrink-0">
        <div className="flex items-center gap-3">
          <div className="relative">
            {animationsEnabled && (
              <div className="absolute inset-0 bg-gradient-to-r from-[hsl(var(--ai-primary))] to-[hsl(var(--ai-accent))] rounded-full blur-md opacity-50 ai-pulse" />
            )}
            <div className="relative bg-gradient-to-r from-[hsl(var(--ai-primary))] to-[hsl(var(--ai-accent))] p-2 rounded-full">
              <Sparkles className="h-4 w-4 text-white ai-float" aria-hidden="true" />
            </div>
          </div>
          <div>
            <h3 className="font-bold text-lg bg-gradient-to-r from-[hsl(var(--ai-primary))] to-[hsl(var(--ai-accent))] bg-clip-text text-transparent">
              Insights IA
            </h3>
            <Badge variant="secondary" className="text-xs mt-1 bg-[hsl(var(--ai-primary)/0.1)] border-[hsl(var(--ai-primary)/0.3)]">
              Analyse complète
            </Badge>
          </div>
        </div>
        <div className="flex items-center gap-1">
          {onRegenerate && (
            <Button
              variant="ghost"
              size="sm"
              onClick={onRegenerate}
              className="h-8 px-2 hover:bg-[hsl(var(--ai-primary)/0.1)] transition-all duration-300"
              aria-label="Régénérer l'analyse"
              title="Régénérer l'analyse"
            >
              <Sparkles className="h-4 w-4 text-[hsl(var(--ai-primary))]" />
            </Button>
          )}
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setAnimationsEnabled(!animationsEnabled)}
            className="h-8 w-8 p-0 hover:bg-[hsl(var(--ai-primary)/0.1)] transition-all duration-300"
            aria-label={animationsEnabled ? "Désactiver les animations" : "Activer les animations"}
            title={animationsEnabled ? "Désactiver les animations" : "Activer les animations"}
          >
            {animationsEnabled ? (
              <Zap className="h-4 w-4 text-[hsl(var(--ai-primary))]" />
            ) : (
              <ZapOff className="h-4 w-4 text-muted-foreground" />
            )}
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={onClose}
            className="h-8 w-8 p-0 hover:bg-[hsl(var(--ai-primary)/0.1)] transition-all duration-300"
            aria-label="Fermer les insights IA"
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
      </div>
      
      <ScrollArea className="relative flex-1 pr-2">
        <div className="space-y-3" role="list" aria-label="Insights générés par IA">
          {insightPoints.map((point, index) => {
            const { type, icon: Icon, color } = categorizeInsight(point);
            return (
              <div
                key={index}
                className={`group flex gap-3 p-3 rounded-lg bg-card/50 backdrop-blur-sm border border-border/50 hover:border-[hsl(var(--ai-primary)/0.3)] hover:bg-[hsl(var(--ai-primary)/0.05)] transition-all duration-300 ${animationsEnabled ? 'hover:scale-[1.02]' : ''}`}
                role="listitem"
                style={animationsEnabled ? { animationDelay: `${index * 100}ms` } : undefined}
              >
                <Icon className={`h-5 w-5 ${color} flex-shrink-0 mt-0.5 ${animationsEnabled ? 'group-hover:scale-110' : ''} transition-transform duration-300`} aria-hidden="true" />
                <span 
                  className="text-sm text-foreground/90 leading-relaxed"
                  dangerouslySetInnerHTML={{ __html: parseMarkdown(point) }}
                />
              </div>
            );
          })}
        </div>
      </ScrollArea>

      {/* Powered by AI badge */}
      <div className="relative mt-4 pt-3 border-t border-border/50 flex-shrink-0">
        <div className="flex items-center justify-center gap-2 text-xs text-muted-foreground">
          <Sparkles className="h-3 w-3" />
          <span>Propulsé par IA avancée</span>
        </div>
      </div>
    </Card>
  );
};
